import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import env from "../config";
import SsoHandler from "../utils/SSOhandler";
import {
  SUPER_ADMIN_AGENCY_CONFIG,
  SUPER_ADMIN_AGENCY_INFO,
  isSuperAdminAgency,
} from "../utils/agencyConfig";
import moment from "moment-timezone";

const UsersContext = createContext();

const useUsersContext = () => useContext(UsersContext);
const BASE_URL = `${env.API_URL}/v1`;
const FACEBOOK_AUTH = env.FACEBOOK_AUTH;

const UsersProvider = ({ children, match }) => {
  const { SSO, checkSSO, setssodata } = SsoHandler();

  // const socket = useSocketContext();
  const [isActiveSide, setIsActiveSide] = useState(true);
  const [processing, setProcessing] = useState(true);
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [hideloader, sethideloader] = useState(false);
  const [userTyping, setUserTyping] = useState(false);
  const [streamResponse, setStreamResponse] = useState("");
  const [isStreaming, setIsStreaming] = useState(true);
  const [fromPromptMsg, setFromPromptMsg] = useState({});
  const [agencyConfig, setAgencyConfig] = useState({});
  const [agencyInfo, setAgencyInfo] = useState({});
  const [errorType, setErrorType] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loadingStateslide, setLoadingStateslide] = useState({});
  const [chats, setchats] = useState(users);
  const [fileLoading, setFileLoading] = useState(false);
  const [agentEditMode, setAgentEditMode] = useState(false);
  const [isAuto, setIsAuto] = useState(SSO?.isAuto);
  const [showExploreContent, setShowExploreContent] = useState(true);
  const [agencyLoading, setAgencyLoading] = useState(true);
  const [conversation, setConversation] = useState([]);

  const getUrlParameter = (name) => {
    // 1. Try query string first
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get(name);
    if (searchParam) return searchParam;

    // 2. Try pathname: /agency_id=superYxadminagency_id or /something/agency_id=superYxadminagency_id
    const pathname = window.location.pathname;
    // Match /agency_id=superYxadminagency_id or /.../agency_id=superYxadminagency_id
    const pathMatch = pathname.match(new RegExp(`${name}=([^&/]+)`));
    return pathMatch ? pathMatch[1] : null;
  };

  const SuperAdminAgency = getUrlParameter("agency_id");

  const getThreadIdFromUrl = () => {
    // First try URLSearchParams for standard query string
    const urlParams = new URLSearchParams(window.location.search);
    let threadId = urlParams.get("thread_id");

    if (!threadId) {
      // If not found, check the pathname for the format /agency_id=...&thread_id=...
      const pathname = window.location.pathname;
      const threadIdMatch = pathname.match(/thread_id=([^&]+)/);
      threadId = threadIdMatch ? threadIdMatch[1] : null;
    }

    return threadId;
  };

  const threadId = getThreadIdFromUrl();

  const getConversationId = async () => {
    const response = await axios.get(
      `${BASE_URL}/message/conversation?conversation_id=${threadId}`,
    );

    if (response?.data?.success) {
      const oldMessage = response?.data?.data?.map((message) => {
        return {
          ...message.message,
        };
      });

      setConversation(oldMessage);
    }
  };

  useEffect(() => {
    if (threadId) {
      getConversationId();
    }
  }, []);


  const getAgencyConfig = async (SSO_data) => {
    if (SuperAdminAgency === "superYxadminagency_id") {
      setAgencyConfig(SUPER_ADMIN_AGENCY_CONFIG);
      setAgencyInfo(SUPER_ADMIN_AGENCY_INFO);
      setAgencyLoading(false);
      return;
    }
    const agencyId = SSO_data?.agency_id || SuperAdminAgency;
    setAgencyLoading(true);

    if (isSuperAdminAgency(agencyId)) {
      setAgencyConfig(SUPER_ADMIN_AGENCY_CONFIG);
      setAgencyInfo(SUPER_ADMIN_AGENCY_INFO);
      setAgencyLoading(false);
      return;
    }

    if (!agencyId) {
      setAgencyConfig({});
      setAgencyInfo({});
      setAgencyLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/agency/agencyconfig?_id=${agencyId}`,
      );

      if (response) {
        setAgencyConfig(response?.data?.data);
        setAgencyInfo(response?.data?.agencyInfo);
      }
    } catch (error) {
      setAgencyConfig({});
      setAgencyInfo({});
    } finally {
      setAgencyLoading(false);
    }
  };

  const [toast, setToast] = useState({ message: "", isVisible: false });
  const [toastStyles, setToastStyles] = useState({});

  const showToast = (message, type = "") => {
    // Determine the background color based on the type
    let backgroundColor = "";
    if (type === "err") {
      backgroundColor = "#FF474D";
    } else if (type === "success") {
      backgroundColor = "#9CFF23";
    }

    // Update the toast styles and set the message
    setToastStyles({ background: backgroundColor });
    setToast({ message, isVisible: true });

    // Hide the toast after 10 seconds
    setTimeout(() => setToast({ message: "", isVisible: false }), 10000);
  };

  const hideToast = () => {
    setToast({ message: "", isVisible: false });
  };

  const setUserAsTyping = (data) => {
    const { userId } = data;
    setUserTyping(true);
  };
  const setUserAsNotTyping = (data) => {
    const { userId } = data;
    setUserTyping(false);
  };


  function processLinks(response) {
    response = response.replace(/[\[\]\(\)]/g, " ");

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    response = response.replace(urlRegex, (url) => {
      url = url.replace(/\)$/, "");

      if (url.includes("Export?id")) {
        return `<span style='color:#34b0cb !important;cursor:pointer' onclick="event.preventDefault(); var link = document.createElement('a'); link.href = '${url}'; link.download = ''; document.body.appendChild(link); link.click(); document.body.removeChild(link);">Click to download</span>`;
      } else {
        return `<a style='color:#34b0cb !important;cursor:pointer' href="${url}" target="_blank">Click to view</a>`;
      }
    });

    return response;
  }

  const extractMessageText = (rawStream) => {
    const lines = rawStream
      .split("\n")
      .filter((line) => line.startsWith("data: "));

    for (const line of lines) {
      try {
        const json = JSON.parse(line.replace("data: ", ""));

        if (json.event === "thread.message.completed") {
          const contentArray = json.data?.content;
          const textObject = contentArray?.find((item) => item.type === "text");
          const value = textObject?.text?.value;

          return value;
        }
      } catch (e) {
        console.warn("Invalid JSON line skipped:", line);
      }
    }

    return null;
  };

  const fetchMessageResponse_old = async (data, loadingIndex = "") => {
    const { userId, response, messagetype, fileData, assistant } = data;
    let pendingRequests = 0;

    if (!loadingIndex) {
      setUserAsTyping(data);
    }

    var msg = "";
    let msg_data = JSON.stringify({
      conversation_id: threadId ? threadId : userId,
      profile_id:
        SuperAdminAgency === "superYxadminagency_id"
          ? "9DITYaucH469so88pN8a"
          : agencyInfo?.agentBuilderAccount?.id,
      user_name: SSO?.chat_username,
      prompt: response.content,
      file_ids: fileData?.map((item) =>
        item.id !== null && item.id !== undefined ? item.id : item.name,
      ),
      assistant_model_name: assistant?.modal_name,
      assistant_id:
        SuperAdminAgency === "superYxadminagency_id"
          ? "asst_FPOU0HcPwzIwKDC2UrmH1IAz"
          : agencyInfo?.agentBuilderAssistantId,
      user_id:
        SuperAdminAgency === "superYxadminagency_id"
          ? "675d890df1dc597eff1d53be"
          : agencyInfo?.agentBuilderUser?.userId || agencyInfo?.agentBuilderUser?._id,
      assistant_name: assistant.assistant_name,
      timezone: moment.tz.guess(),
    });

    let responses;
    try {
      pendingRequests++;

      responses = await axios.post(`${BASE_URL}/chat/message`, {
        data: msg_data,
        defualtModel: assistant?.modal_name,
        messagetype: messagetype,
        prompt: response?.content,
        agency_id:
          SuperAdminAgency === "superYxadminagency_id"
            ? "superYxadminagency_id"
            : agencyInfo?.agentBuilderAccount?.agency_id,
        isAuto: false,
      });

      if (responses) {
        if (responses?.data?.data?.reply) {
          msg = processLinks(responses?.data?.data?.reply);
          setUserAsNotTyping(data);
          if (loadingIndex) {
            setLoadingStateslide((prev) => ({
              ...prev,
              [loadingIndex]: false,
            }));
          }
        } else if (
          !responses?.data?.data?.data &&
          responses?.data?.data?.includes("thread.run.created")
        ) {
          const rawData = responses?.data?.data;
          msg = extractMessageText(rawData);
          setUserAsNotTyping(data);
          if (loadingIndex) {
            setLoadingStateslide((prev) => ({
              ...prev,
              [loadingIndex]: false,
            }));
          }
        } else {
          msg = processLinks(responses?.data?.data?.data);
          setUserAsNotTyping(data);
          if (loadingIndex) {
            setLoadingStateslide((prev) => ({
              ...prev,
              [loadingIndex]: false,
            }));
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      pendingRequests--;
    }

    const newMsgObject = {
      content: msg,
      sender: userId,
      time: new Date().toLocaleTimeString(),
      status: null,
      assistant_name:
        responses?.data?.data?.data?.assistant_name || "agent builder",
      plugins_used: responses?.data?.data?.data?.plugins_used || [],
    };
    const payload = {
      message: newMsgObject,
      conversation_id: threadId ? threadId : userId,
    };

    try {
      pendingRequests++;

      const resp = await axios.post(BASE_URL + "/message/response", { ...payload });
      if (resp?.data?.data?.message)
        setConversation((prev) => [...prev, resp?.data?.data?.message]);

      if (threadId) {
        await getConversationId();
      }
    } catch (error) {
      console.log(error);
    } finally {
      pendingRequests--;
    }

    sethideloader(false);
    setFileLoading(false);
  };

  const fetchMessageResponse = async (data, loadingIndex = "") => {
    if (!loadingIndex) {
      setUserAsTyping(data);
    }

    fetchMessageResponse_old(data, loadingIndex);
  };

  const justToSaveMsg = async (msg, userId) => {
    let userIndex = users.findIndex((user) => user.id === userId);
    const usersCopy = JSON.parse(JSON.stringify(users));
    const newMsgObject = {
      content: msg,
      sender: userId,
      time: new Date().toLocaleTimeString(),
      status: null,
    };
    const payload = {
      message: newMsgObject,
      conversation_id: threadId ? threadId : userId,
    };
    await axios
      .post(BASE_URL + "/message/response", { ...payload })
      .then(async function (response) { })
      .catch(function (error) { });
    if (usersCopy[userIndex]?.messages) {
      var key = Object.keys(usersCopy[userIndex]?.messages);
      key = key[0];
      usersCopy[userIndex].messages[key].push(newMsgObject);
      setUsers(usersCopy);
    }
    sethideloader(false);
    setFileLoading(false);
  };


  const onLoad = (SSO) => {
    if (SSO?.id && SSO?.userUUID) {
      getAgencyConfig(SSO);
    }
  };
  useEffect(() => {
    onLoad(SSO);
  }, [SSO]);


  useEffect(() => {
    checkSSO();
  }, []);

  const ChatSubmit = async (payload, fromPrompt = false) => {
    return await axios
      .post(BASE_URL + "/chat", {
        userId: {
          id:
            SuperAdminAgency === "superYxadminagency_id"
              ? "675d890df1dc597eff1d53be"
              : agencyInfo?.agentBuilderUser?.userId,
          type: "author",
          username: SSO?.chat_username,
        },
        ...payload,
        locationId:
          SuperAdminAgency === "superYxadminagency_id"
            ? "9DITYaucH469so88pN8a"
            : agencyInfo?.agentBuilderAccount?.id,
        isAuto: false,
        timezone: moment.tz.guess(),
      })
      .then(async function (response) {
        var chat_data = {};
        chat_data["Welcome " + response.data.data.username] = [];
        const chat = {
          id: threadId ? threadId : response.data.data.conversation_id,
          locationId: response.data.data.locationId,
          userId: response.data.data.userId,
          name: "",
          whatsapp_name: response.data.data.username,
          messages: chat_data,
          welcome_array: "Welcome " + response.data.data.username,
          fromPrompt: fromPrompt,
        };
        return chat;
      })
      .catch(function (error) {
        return error;
      });
  };

  const addNewMessage = async (
    userId,
    message,
    prompt,
    type,
    selectedFiles = [],
    assistant,
    messagetype,
    fileType = "",
  ) => {
    if (selectedFiles && selectedFiles.length > 0) {
      setFileLoading(true);
    }

    const fileData = selectedFiles?.map((file) => ({
      id: file.id,
      name: file.name,
    }));

    const newMsgObject = {
      content: message,
      sender: null,
      time: new Date().toLocaleTimeString(),
      status: "delivered",
      file_ids: fileData,
      file_type: fileType, // KB | PNG
      assistant_name: "agent builder" || "",
    };

    const payload = {
      message: newMsgObject,
      conversation_id: threadId ? threadId : userId,
      type: type,
    };

    await axios
      .post(BASE_URL + "/message", { ...payload })
      .then(async function (response) {
        setConversation((prev) => [...prev, newMsgObject]);

        if (threadId) {
          await getConversationId();
        }

        if (prompt !== undefined) {
          fetchMessageResponse({
            userId: userId,
            response: { content: prompt },
            messagetype: messagetype,
            fileData: fileData,
            assistant: assistant,
          });
        } else {
          fetchMessageResponse({
            userId: userId,
            response: { content: message },
            messagetype: messagetype,
            fileData: fileData,
            assistant: assistant,
          });
        }
      })
      .catch(function (error) {
        console.log(error?.response?.data?.message, "largeError");

        // Only redirect to pricing if user is not on free plan
        if (SSO?.type !== "free") {
          window.parent.postMessage({ action: "redirect-pricing" }, "*");
          showToast(error?.response?.data?.message);
        }

        if (error.response.data.errorType === "balance") {
          showToast(error.response.data.message, "err");
          setErrorType(true);
          setErrorMsg(error.response.data.message);
        }
      });
  };

  const contextuploadFilefrominput = async (
    files,
    chatId = "",
    setSpinnerLoading,
  ) => {
    // return 0
    const audioformData = new FormData();
    const formData = new FormData();
    const audiofilestype = [
      "audio/mpeg", // mp3
      "video/mp4", // mp4
      "video/mpeg", // mpeg
      "audio/mp4", // m4a
      "audio/wav", // wav
    ];
    const audiofiles = [];
    const otherFiles = [];
    let audioTranscript = "";
    let fileError = false;
    let errorMessage = "";

    for (let index = 0; index < files.length; index++) {
      const fileType = files[index].file?.type;

      if (audiofilestype.includes(fileType)) {
        audiofiles.push(files[index]);
      } else {
        otherFiles.push(files[index]);
      }
    }

    // Append allowed files to formData

    for (let file of audiofiles) {
      audioformData.append("file", file.file ? file.file : file);
      audioformData.append("translate", file.isChecked);
    }
    for (let file of otherFiles) {
      formData.append("file", file.file ? file.file : file);
    }
    audioformData.append(
      "profile_id",
      SuperAdminAgency === "superYxadminagency_id"
        ? "9DITYaucH469so88pN8a"
        : agencyInfo?.agentBuilderAccount?.id,
    );
    audioformData.append("username", SSO?.chat_username);
    audioformData.append("model_name", "openai/gpt-4.1");
    formData.append(
      "profile_id",
      SuperAdminAgency === "superYxadminagency_id"
        ? "9DITYaucH469so88pN8a"
        : agencyInfo?.agentBuilderAccount?.id,
    );
    formData.append("username", SSO?.chat_username);
    formData.append("model_name", "openai/gpt-4.1");

    if (audiofiles.length > 0) {
      try {
        const response = await axios.post(
          `${BASE_URL}/chat/multimedia`,
          audioformData,
          {
            "Content-Type": "multipart/form-data", // Optional: axios sets this automatically
          },
        );
        if (response) {
          return response;
        }
        audioTranscript = response;
      } catch (error) {
        console.log(error);
        fileError = true;
      }
    }

    if (otherFiles.length > 0) {
      try {
        const response = await axios.post(
          `${BASE_URL}/chat/multimedia`,
          formData,
          {
            "Content-Type": "multipart/form-data", // Optional: axios sets this automatically
          },
        );
        if (response) {
          setSpinnerLoading(false);
          return response;
        }
      } catch (error) {
        setSpinnerLoading(false);
        fileError = true;
        errorMessage = error?.response?.data?.message;
      }
    }
    if (fileError) {
      setSpinnerLoading(false);
      return {
        status: fileError,
        transcript: audioTranscript,
        message: errorMessage,
      };
    } else {
      setSpinnerLoading(false);
      return {
        status: "uploaded",
        transcript: audioTranscript,
      };
    }
  };

  const createMemory = async (profileId, content) => {
    let data = JSON.stringify({
      content: content,
    });
    try {
      const response = await axios.post(`${BASE_URL}/chat/assistants/memory`, {
        profile_id: profileId,
        data: data,
      });
      if (response) {
        return response.data.data;
      }
    } catch (error) {
      return error;
    }
  };

  const updateMemory = async (profileId, content) => {
    let data = JSON.stringify({
      content: content,
    });
    try {
      const response = await axios.put(`${BASE_URL}/chat/assistants/memory`, {
        profile_id: profileId,
        data: data,
      });
      if (response) {
        return response.data.data;
      }
    } catch (err) {
      return "error";
    }
  };

  const deleteMemory = async (profileId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/chat/assistants/memory/delete`,
        {
          profile_id: profileId,
        },
      );
      if (response) {
        return response.data.data;
      }
    } catch (error) {
      return error;
    }
  };

  const getMemory = async (profileId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/chat/assistants/memory?profile_id=${profileId}`,
      );
      if (response) {
        return response.data.data;
      }
    } catch (error) {
      return error;
    }
  };

  return (
    <UsersContext.Provider
      value={{
        setFileLoading,
        fileLoading,
        chats,
        setchats,
        users,
        SSO,
        data,
        setUsers,
        hideloader,
        sethideloader,
        addNewMessage,
        contextuploadFilefrominput,
        processing,
        userTyping,
        isActiveSide,
        toast,
        showToast,
        hideToast,
        showExploreContent,
        fromPromptMsg,
        agencyConfig,
        agencyInfo,
        errorType,
        setErrorType,
        errorMsg,
        justToSaveMsg,
        ChatSubmit,
        getMemory,
        createMemory,
        updateMemory,
        deleteMemory,
        checkSSO,
        fetchMessageResponse,
        setLoadingStateslide,
        loadingStateslide,
        streamResponse,
        isStreaming,
        setssodata,
        setAgentEditMode,
        agentEditMode,
        setIsAuto,
        isAuto,
        setAgencyLoading,
        agencyLoading,
        threadId,
        getConversationId,
        conversation,
        setConversation,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export { useUsersContext, UsersProvider };
